import { Button, Flex, Text } from '@mantine/core';
import { useMemo, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { FileUploadInput, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useFileUploadActions } from 'src/data-capture/hooks/file-uploads/useFileUploadActions';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import SelectableItemList from 'src/UI/drawer-flow/SelectableItemList';
import { ReportData } from '../SuitabilityFlow';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import pluralize from 'pluralize';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

interface SelectFilesStepProps extends StepProps<ReportData> {
  title: string;
  description: string;
  hint?: string;
  selectedFileIds: string[];
  setSelectedFileIds: (fileIds: string[]) => void;
  filterFiles?: (files: UploadedFile[]) => UploadedFile[];
  footer?: React.ReactNode;
}

export default function SelectFilesStep(props: SelectFilesStepProps) {
  const { selectedFileIds, setSelectedFileIds, data } = props;
  const { files, isLoading } = useFileUploads({
    clientId: data.client?.id,
  });
  const items = useMemo(() => {
    let filteredFiles = files;
    if (props.filterFiles) {
      filteredFiles = props.filterFiles(files);
    }

    return filteredFiles.map(file => ({
      id: file.id,
      title: file.file.fileName,
      description: `Uploaded: ${new Date(file.file.createdAt).toLocaleDateString()}`,
    }));
  }, [files]);
  const { uploadFiles } = useFileUploadActions({ clientId: data.client?.id });
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;

    if (fileList) {
      setIsUploading(true);
      try {
        const payload: FileUploadInput[] = Array.from(fileList).map(file => ({
          file,
          isResolved: true,
        }));
        const result = await uploadFiles(payload);
        const newUploadedFileIds: string[] = result.map(response => response.data.id);
        setSelectedFileIds([...selectedFileIds, ...newUploadedFileIds]);
      } catch (error) {
        notifyAndLogError(
          'Failed to upload files',
          'An error occurred while uploading files. Please try again.',
          error,
        );
      }
      setIsUploading(false);
    }

    event.target.value = '';
  };

  return (
    <DrawerFlowScreenLayout
      {...props}
      title={props.title}
      description={props.description}
      isLoading={isLoading}
      showClientName={false}
    >
      <Flex direction="column" gap="xs" mih={0}>
        <Flex justify="space-between" align="center">
          <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black" data-testid="document-count">
            {items.length} {pluralize('document', items.length)} found
          </Text>
          <Button component="label" variant="subtle" loading={isUploading}>
            Upload
            <input type="file" multiple style={{ display: 'none' }} onChange={handleFileUpload} />
          </Button>
        </Flex>
        <SelectableItemList
          items={items}
          selectedItemIds={selectedFileIds}
          setSelectedItemIds={setSelectedFileIds}
          hint={props.hint}
          selectMultiple
        />
      </Flex>
    </DrawerFlowScreenLayout>
  );
}
